import React, { useEffect, useState, useContext } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import SignInButton from "./SignInButton";
import { firestore } from "../shared/FirebaseConfig";
import { UserContext } from "../shared/UserContext";
import { SIGNOUTS, signInMany } from "./signoutData";
import { avatarColor, initials } from "../shared/avatar";
import { locStyle } from "../shared/signoutLocations";
import "./ActiveSignOuts.css";

function getElapsed(timestamp, now) {
  const mins = Math.floor((now - new Date(timestamp)) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min";
  return `${mins} min`;
}

const DisplaySignOut = ({ requireIdOnReturn }) => {
  const [signOutData, setSignOutData] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [signingInAll, setSigningInAll] = useState(false);
  const { user } = useContext(UserContext);
  const uid = user?.uid;

  // Update every 15 seconds so elapsed times stay accurate on a glance
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  // Real-time listener — all active sign-outs this teacher created (any class)
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      query(collection(firestore, SIGNOUTS), where("teacherUID", "==", uid)),
      snap => setSignOutData(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [uid]);

  const handleSignInAll = async () => {
    if (!signOutData.length) return;
    if (!window.confirm(`Sign all ${signOutData.length} student${signOutData.length === 1 ? "" : "s"} back in?`)) return;
    setSigningInAll(true);
    try {
      await signInMany(signOutData.map(s => s.id));
    } catch (err) {
      console.error("Error signing in all students:", err);
    } finally {
      setSigningInAll(false);
    }
  };

  // Sort by longest out first
  const sorted = [...signOutData].sort(
    (a, b) => new Date(a.time_out) - new Date(b.time_out)
  );

  return (
    <div className="display-sign-out-container">
      <div className="signout-header">
        <h2 style={{ margin: 0 }}>
          Active Sign-Outs
          {signOutData.length > 0 && (
            <span className="signout-count">{signOutData.length} out</span>
          )}
        </h2>
        {signOutData.length > 0 && !requireIdOnReturn && (
          <button
            className="signout-signin-all"
            onClick={handleSignInAll}
            disabled={signingInAll}
          >
            {signingInAll ? "Signing in…" : "Sign In All"}
          </button>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="signout-empty">No students currently signed out.</div>
      ) : (
        <div className="signout-list">
          {sorted.map((s) => {
            const mins = Math.floor((now - new Date(s.time_out)) / 60000);
            const isLong = mins >= 10;
            const ls = locStyle(s.location);
            return (
              <div
                key={s.id}
                className={`signout-card ${isLong ? "signout-card--long" : ""}`}
              >
                <span
                  className="signout-avatar"
                  style={{ background: avatarColor(s.studentName) }}
                >
                  {initials(s.studentName)}
                </span>
                <span className="signout-body">
                  <span className="signout-name">{s.studentName}</span>
                </span>
                <span
                  className="signout-loc"
                  style={{ background: ls.bg, color: ls.color }}
                >
                  {ls.emoji} {s.location}
                </span>
                <span className={`signout-time ${isLong ? "is-long" : ""}`}>
                  {getElapsed(s.time_out, now)}
                </span>
                <span className="signout-action">
                  <SignInButton
                    id={s.id}
                    studentName={s.studentName}
                    idNumber={s.idNumber}
                    requireIdOnReturn={requireIdOnReturn}
                    onSignInAll={handleSignInAll}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DisplaySignOut;
